<template>
  <div class="bio-primary-link-wrap" :class="{'bio-force-mobile': forceMobile}" v-if="item">
    <button
      ref="button"
      type="button"
      class="bio-primary-link"
      :style="{background: backgroundColor, color: textColor}"
      @click="$emit('open')"
      @mouseenter="onHoverEnter"
      @mouseleave="onHoverLeave"
      @mousedown="onPressStart"
      @mouseup="onPressEnd"
      @touchstart="onPressStart"
      @touchend="onPressEnd"
    >
      <!-- Leading icon/photo, type-aware per feedback: IMAGE shows the
           link's own photo in a circle instead of an icon, INSTAGRAM shows
           the live-fetched profile photo with the Instagram glyph
           overlapping it (same overlap pattern as .bio-brand-cta-avatars),
           YOUTUBE/WHATSAPP get their brand icon+color instead of the
           generic item.icon. Everything else keeps the original
           item.icon/mdi-link-variant fallback. -->
      <v-avatar v-if="isImageType" size="32" class="bio-primary-link-icon">
        <v-img v-if="item.image && item.image.src && !(isImageBroken && isImageBroken(item.image.src))" :src="item.image.src" eager :transition="false" @error="markImageBroken && markImageBroken(item.image.src)"/>
        <v-icon v-else size="18" :color="textColor">mdi-image-off-outline</v-icon>
      </v-avatar>
      <span v-else-if="isInstagramType" class="bio-primary-link-ig-avatars">
        <v-avatar size="32" class="bio-primary-link-ig-photo" :color="instagramAvatarSrc ? undefined : '#E1306C'">
          <v-img v-if="instagramAvatarSrc" :src="instagramAvatarSrc" eager :transition="false" @error="handleInstagramAvatarError"/>
          <v-icon v-else size="16" color="#FFFFFF">mdi-instagram</v-icon>
        </v-avatar>
        <span class="bio-primary-link-ig-badge">
          <v-icon size="11" color="#E1306C">mdi-instagram</v-icon>
        </span>
      </span>
      <v-icon v-else size="20" :color="leadingIconColor" class="bio-primary-link-icon">{{ leadingIcon }}</v-icon>
      <!-- Starts collapsed (icon-only) and expands to reveal the label,
           description and arrow together, once the button itself has
           finished popping in - per feedback, the entrance animation is
           "icon only, then the full content" (see playEntrance below).
           maxWidth (not width:'auto') is what's actually animated, since
           GSAP can't tween to an intrinsic 'auto' size - a generous fixed
           target plus overflow:hidden gets the same visual result. -->
      <span ref="reveal" class="bio-primary-link-reveal">
        <span class="bio-primary-link-text">
          <b class="bio-primary-link-label">{{ primaryLabel }}</b>
          <span v-if="primaryDescription" class="bio-primary-link-desc">{{ primaryDescription }}</span>
          <span v-if="primaryDescriptionSubtle" class="bio-primary-link-desc-subtle">{{ primaryDescriptionSubtle }}</span>
        </span>
        <span v-if="isInstagramType" class="bio-primary-link-follow">{{ $t('follow') }}</span>
        <v-icon v-else size="20" :color="textColor" class="ml-2">mdi-arrow-right</v-icon>
      </span>
    </button>
  </div>
</template>

<script>
import { gsap } from 'gsap'
import { apiV1OpenGraph } from '../../../service/resources/api-v1-open-graph'

export default {
  name: 'BioPrimaryLink',
  props: {
    // Same biographyHasOnlineAddress shape as the content link cards, so it
    // carries whatever icon/description the user already set for this link
    // in the editor (see SimpleLinkCard.vue for the same item.icon pattern).
    item: { type: Object, default: null },
    backgroundColor: { type: String, default: '#111111' },
    textColor: { type: String, default: '#FFFFFF' },
    isImageBroken: { type: Function, default: null },
    markImageBroken: { type: Function, default: null },
    // See the matching comment on BiographySmartphoneView.vue's own
    // forceMobile prop - editor preview only, never set on the real page.
    forceMobile: { type: Boolean, default: false },
  },
  data: function() {
    return {
      prefersReducedMotion: !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches),
      instagramAvatarBroken: false,
      // Best-effort live enrichment - see InstagramLinkCard.vue for the
      // same fetch/parse approach (kept as its own copy here rather than
      // a shared mixin, since the primary link's display needs are
      // simpler - just photo/name/followers, no separate loading skeleton).
      instagramProfile: null,
    }
  },
  computed: {
    isImageType: function() {
      return this.item && this.item.type === 'IMAGE'
    },
    isYoutubeType: function() {
      return this.item && this.item.type === 'YOUTUBE'
    },
    isWhatsappType: function() {
      return this.item && this.item.type === 'WHATSAPP'
    },
    isInstagramType: function() {
      return this.item && this.item.type === 'INSTAGRAM'
    },
    leadingIcon: function() {
      if (this.isYoutubeType) return 'mdi-youtube'
      if (this.isWhatsappType) return 'mdi-whatsapp'
      return (this.item && this.item.icon) || 'mdi-link-variant'
    },
    leadingIconColor: function() {
      if (this.isYoutubeType) return '#FF0000'
      if (this.isWhatsappType) return '#25D366'
      return this.textColor
    },
    primaryLabel: function() {
      if (this.isInstagramType) return (this.instagramProfile && this.instagramProfile.name) || (this.item.title ? `@${this.item.title}` : this.$t('primaryLink'))
      if (this.item.title) return this.item.title
      // A generic "Link principal" fallback read oddly on a YouTube-typed
      // primary link with no title set - a type-aware CTA fits better
      // than the same catch-all every other type falls back to.
      if (this.isYoutubeType) return this.$t('watchOnYoutube')
      return this.$t('primaryLink')
    },
    // Instagram's own stats line (followers) - kept separate from
    // primaryDescriptionSubtle below, which is the user's own configured
    // description, per feedback ("deve ter a descrição mais discreta em
    // algum lugar do card") - it was being dropped entirely before.
    primaryDescription: function() {
      if (this.isInstagramType) {
        if (!this.instagramProfile || !this.instagramProfile.followers) return null
        return `${this.instagramProfile.followers} ${this.$t('instagramFollowers')}`
      }
      return this.item.description
    },
    primaryDescriptionSubtle: function() {
      if (!this.isInstagramType) return null
      return this.item.description
    },
    instagramUsername: function() {
      const url = this.item.onlineAddress && this.item.onlineAddress.url
      const match = url && url.match(/instagram\.com\/([^/?#]+)/i)
      return (match && match[1]) || this.item.title || null
    },
    instagramAvatarSrc: function() {
      if (this.instagramProfile && this.instagramProfile.image && !this.instagramAvatarBroken) return this.instagramProfile.image
      return null
    },
  },
  mounted: function() {
    this.playEntrance()
    if (this.isInstagramType) this.loadInstagramProfile()
  },
  methods: {
    handleInstagramAvatarError: function() {
      this.instagramAvatarBroken = true
    },
    loadInstagramProfile: async function() {
      if (!this.instagramUsername) return
      try {
        const response = (await apiV1OpenGraph.instagramProfile(this.instagramUsername)).data
        const image = response['og:image'] || null
        const title = response['og:title'] || ''
        const description = response['og:description'] || ''
        const nameMatch = title.match(/^(.*?)\s*\(@/)
        const name = nameMatch ? nameMatch[1].trim() : null
        // Same two-pattern parsing as InstagramLinkCard.vue - Instagram's
        // PT description reorders the clause ("seguindo 1,782", verb
        // before its number) rather than just translating the EN words.
        const numberToken = '(\\d{1,3}(?:[.,]\\d{3})*(?:[.,]\\d+)?\\s?(?:mil|k|m|b)?)'
        const statsMatch =
          description.match(new RegExp(`^${numberToken}\\s+Followers,`, 'i')) ||
          description.match(new RegExp(`^${numberToken}\\s+seguidores,`, 'i'))
        if (image || name || statsMatch) {
          this.instagramProfile = {
            image: image || null,
            name: name || null,
            followers: statsMatch ? statsMatch[1].trim() : null,
          }
        }
      } catch (error) {
        // Best-effort enrichment - falls back to the static @username label.
      }
    },
    playEntrance: function() {
      const button = this.$refs.button
      const reveal = this.$refs.reveal
      if (!button || !reveal) return
      if (this.prefersReducedMotion) {
        gsap.set(button, { scale: 1, opacity: 1 })
        gsap.set(reveal, { maxWidth: 260, opacity: 1, marginLeft: 8 })
        return
      }
      gsap.set(button, { scale: 0, opacity: 0 })
      gsap.set(reveal, { maxWidth: 0, opacity: 0, marginLeft: 0 })
      const tl = gsap.timeline({ delay: 0.2 })
      tl.to(button, { scale: 1, opacity: 1, duration: 0.6, ease: 'back.out(1.7)' })
      tl.to(reveal, { maxWidth: 260, opacity: 1, marginLeft: 8, duration: 0.55, ease: 'power2.out' }, '+=0.5')
    },
    onHoverEnter: function() {
      if (this.prefersReducedMotion) return
      gsap.to(this.$refs.button, { scale: 1.05, duration: 0.25, ease: 'power2.out' })
    },
    onHoverLeave: function() {
      if (this.prefersReducedMotion) return
      gsap.to(this.$refs.button, { scale: 1, duration: 0.25, ease: 'power2.out' })
    },
    onPressStart: function() {
      if (this.prefersReducedMotion) return
      gsap.to(this.$refs.button, { scale: 0.93, duration: 0.1, ease: 'power1.out' })
    },
    onPressEnd: function() {
      if (this.prefersReducedMotion) return
      gsap.to(this.$refs.button, { scale: 1, duration: 0.2, ease: 'power2.out' })
    },
  },
}
</script>

<style scoped>
/* Mobile/tablet (<1024px): position:fixed, because the page scrolls
   normally there and the spec requires the Primary Link to stay visible at
   every scroll position — sticky only pins once its normal-flow position
   would scroll past the offset, so it'd stay hidden through most of a long
   link list. See design.md's "Primary Link stacking" note re: verifying
   this isn't trapped behind an ancestor's own transform/contain (the same
   bug FeatureCallout.vue hit earlier in this project). */
.bio-primary-link-wrap {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 16px;
  z-index: 40;
  display: flex;
  justify-content: center;
  padding: 0 16px;
  pointer-events: none;
}
/* Editor preview (forceMobile): this wrap is a direct .bio-shell child,
   rendered outside .bio-shell-scroll (BiographySmartphoneView.vue) which
   is the element that actually scrolls there - position:absolute anchors
   it to .bio-shell's own bounded box (position:relative, sized to fill
   the mockup) instead of the real page viewport, keeping every other
   value (left/right/bottom/z-index) identical so it still reads as a
   full-width floating footer bar. See .bio-shell-scroll's comment in
   BiographySmartphoneView.vue for why position:fixed + an ancestor
   transform ("trap" it in place) doesn't work for this case. */
.bio-primary-link-wrap.bio-force-mobile {
  position: absolute;
}
.bio-primary-link {
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 56px;
  /* Matches min-height, not a wide fixed value - the button needs to be
     able to start as a genuine (near-)circle around just the icon before
     the entrance reveal animation runs (see playEntrance in the script). */
  min-width: 56px;
  max-width: 100%;
  padding: 0.6rem 18px;
  border: none;
  /* Pill when it's a single line (no description), softer rounding once a
     second line makes the button taller than it is round. */
  border-radius: 999px;
  font-weight: 700;
  font-size: 1.05rem;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  box-shadow: 0 12px 28px rgba(0,0,0,0.28);
}
.bio-primary-link:has(.bio-primary-link-desc) {
  border-radius: 1.5rem;
}
.bio-primary-link-icon {
  flex-shrink: 0;
}
.bio-primary-link-ig-avatars {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}
.bio-primary-link-ig-photo {
  position: absolute;
  left: 0;
  top: 0;
}
.bio-primary-link-ig-badge {
  position: absolute;
  right: -4px;
  bottom: -4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #FFFFFF;
  box-shadow: 0 2px 4px rgba(0,0,0,0.25);
}
.bio-primary-link-reveal {
  display: inline-flex;
  align-items: center;
  overflow: hidden;
}
.bio-primary-link-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  text-align: left;
  line-height: 1.25;
}
.bio-primary-link-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bio-primary-link-desc {
  font-weight: 400;
  font-size: 0.78rem;
  opacity: 0.85;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* The item's own configured description, shown under the Instagram stats
   line - smaller/dimmer than .bio-primary-link-desc, per feedback
   ("mais discreta"). */
.bio-primary-link-desc-subtle {
  font-weight: 400;
  font-size: 0.7rem;
  opacity: 0.65;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bio-primary-link-follow {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.6rem;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.75rem;
  color: #FFFFFF;
  background: linear-gradient(135deg, #833AB4 0%, #E1306C 50%, #FCAF45 100%);
}
/* Desktop (>=1024px): .bio-shell is now a bounded "frame" (fixed aspect
   ratio, capped height — see BiographySmartphoneView.vue) with its own
   content scrolling internally rather than growing the page. Once the
   card itself no longer grows, position:fixed-relative-to-viewport isn't
   needed for "always visible" anymore, and it stops being genuinely
   "inside" the card in any DOM sense. Switching to position:absolute,
   anchored to .bio-shell (which is position:relative), makes it an actual
   part of the container's own box instead of an overlay that merely lines
   up with it. */
@media (min-width: 1024px) {
  .bio-primary-link-wrap:not(.bio-force-mobile) {
    position: absolute;
    left: auto;
    right: 24px;
    bottom: 24px;
    padding: 0;
  }
}
</style>
